import { useToast } from '@contexts/Toast';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import { errorMessage } from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { ServiceBottomSheetProps, ServiceProps } from '@helpers/Models';
import { useEffect, useState } from 'react';

export const ServiceBottomSheet: React.FC<ServiceBottomSheetProps> = ({
  onClose,
  onSelect,
}) => {
  const [serviceOptions, setServiceOptions] = useState<ServiceProps[]>([]);
  const { showToastMessage } = useToast();
  const [selectedRow, setSelectedRow] = useState<ServiceProps>();
  const [searchKey, setSearchKey] = useState('');

  /**
   * Fetches species and service information whenever the search key changes.
   *
   * This effect runs whenever `searchKey` updates, triggering a fetch request
   * to retrieve species and service-related data based on the search criteria.
   *
   * @param {string} searchKey - The search keyword used to filter species and services.
   */
  useEffect(() => {
    fetchSpeciesAndServiceInfo(searchKey);
  }, [searchKey]);

  /**
   * Fetches species and service information based on a search key.
   *
   * This function makes an asynchronous API request to fetch service options
   * using the provided search key. The fetched service data is then stored in
   * the component state. If an error occurs during the request, an appropriate
   * error message is displayed.
   *
   * @param {string} searchKey - The search keyword used to filter service options.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   *
   * @throws Will catch and handle Axios errors by displaying an error toast message.
   */
  const fetchSpeciesAndServiceInfo = async (
    searchKey: string,
  ): Promise<void> => {
    try {
      const serviceOptionsResponse = await Axios.post(
        ApiEndpoints.FETCH_SERVICE,
        { searchKey },
      );
      setServiceOptions(serviceOptionsResponse.data.data.services);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_SERVICES, 'error');
    }
  };

  /**
   * Handles the selection of a row in the service list.
   *
   * This function is triggered when a user clicks on a row. It updates the selected row state,
   * invokes the `onSelect` callback with the selected service, and closes the selection modal.
   *
   * @param {ServiceProps} service - The service object that was clicked.
   */
  const handleRowClick = (service: ServiceProps): void => {
    setSelectedRow(service);
    onSelect(service);
    onClose();
  };

  return (
    <>
      {serviceOptions.length > 0 && (
        <div className="fixed z-10 inset-0 bg-black bg-opacity-40 flex justify-center items-end w-full h-full">
          <div className="bg-white rounded-t-2xl w-full p-6 shadow-lg transition-transform transform translate-y-0 max-h-[70vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h1 className="font-semibold text-lg">Select Services</h1>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    value={searchKey}
                    placeholder="Search..."
                    onChange={(e) => setSearchKey(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-1 h-8 w-80 text-sm"
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  />
                </div>
                <button
                  onClick={onClose}
                  type="button"
                  className="text-gray-600 hover:text-gray-900"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              <ul className="space-y-2">
                {serviceOptions
                  .filter((s) => s.availableFlag == 1 && !s.deletedAt) // 🔽 Exclude soft‑deleted services
                  .map((service) => (
                    <li
                      key={service.id}
                      className={`p-3 rounded-lg cursor-pointer transition ${selectedRow?.id === service.id
                          ? 'bg-tertiary border-primary'
                          : 'hover:bg-gray-100'
                        }`}
                    >
                      <button
                        type="button"
                        className="w-full text-left focus:outline-none"
                        onClick={() => handleRowClick(service)}
                      >
                        <p className="font-semibold">{service.name}</p>
                        <p className="text-sm text-gray-500">
                          {service.description}
                        </p>
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};